import { Close as CloseIcon } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import axios from "axios";
import moment from "moment";
import "moment-timezone";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import EmbeddedImagePicker from "../../components/EmbeddedImagePicker";
import LoadingButton from "../../components/LoadingButton";
import { hostname } from "../../utils";
import { INTEGRATION_LOGOS } from "../integrations/constants";

interface PublishDialogProps {
  open: boolean;
  onClose: () => void;
  content: any;
  companyId: string;
  getToken: () => Promise<string>;
  onError?: (error: string) => void;
}

export default ({
  open,
  onClose,
  content,
  companyId,
  getToken,
  onError,
}: PublishDialogProps) => {
  const [formData, setFormData] = useState<any>({
    publishAsDraft: false,
    publishMode: "now", // "now" or "schedule"
    scheduleDate: moment().add(1, 'day'), // Default to tomorrow
    scheduleHour: 9,
    scheduleMinute: 0,
    scheduleAMPM: "AM",
    scheduleTimezone: moment.tz.guess() // Detect user's timezone
  });
  const [isPublishing, setIsPublishing] = useState(false);
  const [integrations, setIntegrations] = useState<Array<any>>([]);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>("");
  const [scheduleError, setScheduleError] = useState<string>("");
  const navigate = useNavigate();

  // Helper function to get logo for integration type
  const getIntegrationLogo = (integrationType: string) => {
    return INTEGRATION_LOGOS[integrationType?.toLowerCase()] || null;
  };

  const getIntegrations = async () => {
    const currentToken = await getToken();

    // Determine which integration types to fetch based on content type
    let integrationCategory = "blog"; // Default to blog
    let specificType: string | undefined = undefined;

    if (content?.type === "linkedin_post") {
      // Fetch only LinkedIn integrations for LinkedIn posts
      integrationCategory = "linkedin";
    } else if (content?.type === "facebook_post") {
      // Fetch only Facebook integrations for Facebook posts
      integrationCategory = "meta";
      specificType = "facebook";
    } else if (content?.type === "instagram_post") {
      // Fetch only Instagram integrations for Instagram posts
      integrationCategory = "meta";
      specificType = "instagram";
    }

    try {
      const response = await axios.get(
        `${hostname}/integrations/by-type/${integrationCategory}`,
        {
          params: { companyId, type: specificType },
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );

      setIntegrations(response.data.items || []);
    } catch (error) {
      console.error("Error fetching integrations:", error);
      setIntegrations([]);
    }
  };

  const validateSchedule = () => {
    if (formData.publishMode === "schedule") {
      if (!formData.scheduleDate) {
        setScheduleError("Please select a date");
        return false;
      }

      // Convert hour to 24-hour format
      let hour24 = formData.scheduleHour;
      if (formData.scheduleAMPM === "PM" && formData.scheduleHour !== 12) {
        hour24 = formData.scheduleHour + 12;
      } else if (formData.scheduleAMPM === "AM" && formData.scheduleHour === 12) {
        hour24 = 0;
      }

      // Create time in the selected timezone
      const scheduledTimeInTZ = moment.tz(
        formData.scheduleDate.format("YYYY-MM-DD") + ` ${hour24.toString().padStart(2, "0")}:${formData.scheduleMinute.toString().padStart(2, "0")}`,
        "YYYY-MM-DD HH:mm",
        formData.scheduleTimezone
      );
      const now = moment();

      if (scheduledTimeInTZ <= now) {
        setScheduleError("Scheduled time must be in the future");
        return false;
      }
    }
    setScheduleError("");
    return true;
  };

  // Check if image is required for this content type
  const isImageRequired = content?.type === "instagram_post" ||
                          content?.type === "facebook_post" ||
                          content?.type === "linkedin_post";

  const handlePublish = async () => {
    if (!formData.publishAccount) return;

    // Validate image is selected for social media posts
    if (isImageRequired && !formData.imageId) {
      if (onError) {
        onError("Please select an image before publishing. Social media posts require an image.");
      }
      return;
    }

    if (!validateSchedule()) return;

    setIsPublishing(true);
    const currentToken = await getToken();
    try {
      const publishData: any = {
        companyId,
        contentId: content.id,
        publishAccount: formData.publishAccount,
        publishAsDraft: formData.publishAsDraft,
        imageId: formData.imageId,
      };

      // Add schedule datetime if scheduling
      if (formData.publishMode === "schedule" && formData.scheduleDate) {
        // Convert hour to 24-hour format
        let hour24 = formData.scheduleHour;
        if (formData.scheduleAMPM === "PM" && formData.scheduleHour !== 12) {
          hour24 = formData.scheduleHour + 12;
        } else if (formData.scheduleAMPM === "AM" && formData.scheduleHour === 12) {
          hour24 = 0;
        }

        // Create time in the selected timezone and convert to UTC
        const scheduledTimeInTZ = moment.tz(
          formData.scheduleDate.format("YYYY-MM-DD") + ` ${hour24.toString().padStart(2, "0")}:${formData.scheduleMinute.toString().padStart(2, "0")}`,
          "YYYY-MM-DD HH:mm",
          formData.scheduleTimezone
        );
        publishData.scheduleDateTime = scheduledTimeInTZ.utc().toISOString();
        publishData.scheduleTimezone = formData.scheduleTimezone;
      }

      await axios.post(
        `${hostname}/content/${content.id}/publish`,
        publishData,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
      onClose();
    } catch (error: any) {
      // Display error to user via parent callback
      const errorMessage = error.response?.data?.error || error.message || "Failed to publish content";

      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsPublishing(false);
    }
  };

  useEffect(() => {
    if (open) {
      getIntegrations();

      // If content is already scheduled, pre-populate the schedule fields
      if (content?.status === "scheduled" && content?.publishDate) {
        const scheduledMoment = moment(content.publishDate);
        const hour12 = scheduledMoment.hours() % 12 || 12;
        const ampm = scheduledMoment.hours() >= 12 ? "PM" : "AM";

        setFormData((prev: any) => ({
          ...prev,
          publishMode: "schedule",
          scheduleDate: scheduledMoment,
          scheduleHour: hour12,
          scheduleMinute: scheduledMoment.minutes(),
          scheduleAMPM: ampm,
          scheduleTimezone: moment.tz.guess()
        }));
      }
    }
  }, [open, content]);

  if (!open || !content) {
    return <></>;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          width: "90vw",
          maxWidth: "1200px",
          minWidth: "800px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Publish Content - {content.title}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
          💡 Tip: You can publish now or schedule for a later date/time using the options below
        </Typography>
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom>
            Select a main image for your post
            {isImageRequired && (
              <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
            )}
            :
          </Typography>
          {isImageRequired && !selectedImageUrl && (
            <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
              An image is required for social media posts
            </Typography>
          )}

          <EmbeddedImagePicker
            onSelect={(imageUrl, imageId) => {
              setSelectedImageUrl(imageUrl);
              setFormData({ ...formData, imageId });
            }}
            getToken={getToken}
            companyId={companyId}
            contentId={content?.id}
            selectedImage={selectedImageUrl}
            height={500}
          />
        </Box>
        <FormControl fullWidth sx={{ mt: 2, mb: 1 }}>
          <InputLabel>Publish Account</InputLabel>
          <Select
            label="Publish Account"
            value={formData.publishAccount}
            onChange={(e) =>
              setFormData({ ...formData, publishAccount: e.target.value })
            }
            renderValue={(selected) => {
              const integration = integrations.find(i => i.id === selected);
              if (!integration) return "";

              const pageInfo = integration.pageInfo;
              const pagePicture = pageInfo?.pagePicture || pageInfo?.profilePicture;
              const logo = pagePicture || getIntegrationLogo(integration.type);
              const isSocialIntegration = integration.type === "facebook" || integration.type === "instagram";

              // Build display text with page name, username, and instance name
              const displayName = pageInfo?.pageName || pageInfo?.username || integration.name;
              const showUsername = isSocialIntegration && pageInfo?.username && pageInfo?.pageName;
              const showInstanceName = isSocialIntegration && integration?.instanceName;

              return (
                <Box display="flex" alignItems="center" gap={1}>
                  {logo && (
                    <Avatar
                      src={logo}
                      sx={{ width: 24, height: 24 }}
                      variant={pagePicture ? "circular" : "square"}
                    />
                  )}
                  <Typography variant="body2">
                    {displayName}
                    {showUsername && ` (@${pageInfo.username})`}
                    {showInstanceName && ` - ${integration.instanceName}`}
                  </Typography>
                </Box>
              );
            }}
          >
            {integrations.map((integration) => {
              const pageInfo = integration.pageInfo;
              const pagePicture = pageInfo?.pagePicture || pageInfo?.profilePicture;
              const logo = pagePicture || getIntegrationLogo(integration.type);
              const isSocialIntegration = integration.type === "facebook" || integration.type === "instagram";

              return (
                <MenuItem key={integration.id} value={integration.id}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    {logo && (
                      <Avatar
                        src={logo}
                        sx={{ width: 32, height: 32 }}
                        variant={pagePicture ? "circular" : "square"}
                      />
                    )}
                    <Box>
                      <Typography variant="body2">
                        {pageInfo?.pageName || pageInfo?.username || integration.name}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                        {pageInfo?.username && (
                          <Typography variant="caption" color="text.secondary">
                            @{pageInfo.username}
                          </Typography>
                        )}
                        {isSocialIntegration && integration?.instanceName && (
                          <Chip
                            label={integration.instanceName}
                            size="small"
                            sx={{ height: 16, fontSize: "0.65rem" }}
                          />
                        )}
                        {pageInfo?.pageCategory && (
                          <Chip
                            label={pageInfo.pageCategory}
                            size="small"
                            sx={{ height: 16, fontSize: "0.65rem" }}
                          />
                        )}
                        {!isSocialIntegration && integration.type && (
                          <Typography variant="caption" color="text.secondary">
                            {integration.type.charAt(0).toUpperCase() + integration.type.slice(1)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <Box sx={{ mt: 3, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Publishing Options
          </Typography>
          <RadioGroup
            row
            value={formData.publishMode}
            onChange={(e) => {
              setFormData({ ...formData, publishMode: e.target.value });
              setScheduleError("");
            }}
          >
            <FormControlLabel value="now" control={<Radio />} label="Publish Now" />
            <FormControlLabel value="schedule" control={<Radio />} label="Schedule for Later" />
          </RadioGroup>
        </Box>

        {formData.publishMode === "schedule" && (
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box sx={{ mb: 2, p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 2, mb: 2, alignItems: "flex-start" }}>
                {/* Date Picker */}
                <DatePicker
                  label="Select Date"
                  value={formData.scheduleDate}
                  onChange={(newDate) => {
                    setFormData({ ...formData, scheduleDate: newDate });
                    setScheduleError("");
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />

                {/* Hour Dropdown */}
                <FormControl fullWidth>
                  <InputLabel>Hour</InputLabel>
                  <Select
                    label="Hour"
                    value={formData.scheduleHour}
                    onChange={(e) => {
                      setFormData({ ...formData, scheduleHour: e.target.value });
                      setScheduleError("");
                    }}
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((hour) => (
                      <MenuItem key={hour} value={hour}>
                        {hour}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Minute Dropdown */}
                <FormControl fullWidth>
                  <InputLabel>Minute</InputLabel>
                  <Select
                    label="Minute"
                    value={formData.scheduleMinute}
                    onChange={(e) => {
                      setFormData({ ...formData, scheduleMinute: e.target.value });
                      setScheduleError("");
                    }}
                  >
                    {Array.from({ length: 60 }, (_, i) => i).map((minute) => (
                      <MenuItem key={minute} value={minute}>
                        {minute.toString().padStart(2, "0")}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* AM/PM Dropdown */}
                <FormControl fullWidth>
                  <InputLabel>AM/PM</InputLabel>
                  <Select
                    label="AM/PM"
                    value={formData.scheduleAMPM}
                    onChange={(e) => {
                      setFormData({ ...formData, scheduleAMPM: e.target.value });
                      setScheduleError("");
                    }}
                  >
                    <MenuItem value="AM">AM</MenuItem>
                    <MenuItem value="PM">PM</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Timezone Picker */}
              <Box sx={{ mb: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Timezone</InputLabel>
                  <Select
                    label="Timezone"
                    value={formData.scheduleTimezone}
                    onChange={(e) => {
                      setFormData({ ...formData, scheduleTimezone: e.target.value });
                      setScheduleError("");
                    }}
                  >
                    {moment.tz.names().map((tz) => (
                      <MenuItem key={tz} value={tz}>
                        {tz}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {scheduleError && (
                <Typography color="error" variant="caption" display="block" sx={{ mb: 1 }}>
                  {scheduleError}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" display="block">
                Content will be automatically published at the selected date and time in your timezone
              </Typography>
            </Box>
          </LocalizationProvider>
        )}

        {/* Only show publish as draft for integrations that support it (WordPress, Shopify) */}
        {(() => {
          const selectedIntegration = integrations.find(i => i.id === formData.publishAccount);
          const integrationType = selectedIntegration?.type?.toLowerCase();
          const supportsDraft = integrationType === 'wordpress' || integrationType === 'shopify' || integrationType === 'webflow' || integrationType === 'blog';

          if (supportsDraft) {
            return (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.publishAsDraft || false}
                      onChange={(e) =>
                        setFormData({ ...formData, publishAsDraft: e.target.checked })
                      }
                    />
                  }
                  label="Publish as draft"
                  sx={{ mt: 2, mb: 1 }}
                />
                <Typography variant="caption" display="block" sx={{ ml: 4, mb: 2 }}>
                  {formData.publishAsDraft
                    ? "Content will be saved as draft (not publicly visible)"
                    : formData.publishMode === "schedule"
                    ? "Will be saved as scheduled"
                    : "Content will be published immediately"}
                </Typography>
              </>
            );
          } else {
            return (
              <Typography variant="caption" display="block" sx={{ mb: 2 }}>
                {formData.publishMode === "schedule"
                  ? "Content will be saved as scheduled and published at the specified date and time"
                  : "Content will be published immediately"}
              </Typography>
            );
          }
        })()}
        <Typography>
          Click{" "}
          <Link to={`/dashboard/companies/${companyId}/integrations`}>
            here
          </Link>{" "}
          to setup a new integration account
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isPublishing}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handlePublish}
          loading={isPublishing}
          disabled={!formData.publishAccount || (formData.publishMode === "schedule" && !formData.scheduleDate) || (isImageRequired && !formData.imageId)}
        >
          {formData.publishAsDraft
            ? "Save as Draft"
            : formData.publishMode === "schedule"
            ? "Schedule Content"
            : "Publish Now"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
